import React from 'react';
import { TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import type { BudgetUtilizationCardProps } from '../../types/analytics';

/**
 * Format a number as currency.
 */
function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * BudgetUtilizationCard Component
 *
 * Displays budget utilization as a progress bar with current spend vs budget limit.
 * Shows warning when approaching or exceeding budget.
 */
const BudgetUtilizationCard: React.FC<BudgetUtilizationCardProps> = ({
  label,
  currentSpent,
  budgetLimit,
  period,
}) => {
  // Calculate utilization percentage
  const percentage = budgetLimit ? Math.min((currentSpent / budgetLimit) * 100, 100) : 0;
  const isOverBudget = budgetLimit && currentSpent > budgetLimit;
  const isNearBudget = budgetLimit && percentage >= 80 && !isOverBudget;

  // Determine color based on utilization
  const getProgressColor = () => {
    if (isOverBudget) return 'bg-red-500';
    if (isNearBudget) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const getTextColor = () => {
    if (isOverBudget) return 'text-red-600';
    if (isNearBudget) return 'text-yellow-600';
    return 'text-primary-600';
  };

  // Get status icon
  const getStatusIcon = () => {
    if (isOverBudget) {
      return <AlertTriangle className="w-5 h-5 text-red-500" />;
    }
    if (isNearBudget) {
      return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    }
    return <CheckCircle className="w-5 h-5 text-green-500" />;
  };

  // No budget configured
  if (budgetLimit === null || budgetLimit === undefined) {
    return (
      <div
        data-testid={`budget-utilization-${period}`}
        className="bg-white dark:bg-cap-dark rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</h3>
          <TrendingUp className="w-4 h-4 text-gray-400" />
        </div>
        <p className="text-2xl font-semibold text-gray-900 dark:text-warm-white">
          {formatCurrency(currentSpent)}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">No budget limit set</p>
      </div>
    );
  }

  return (
    <div
      data-testid={`budget-utilization-${period}`}
      className="bg-white dark:bg-cap-dark rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</h3>
        {getStatusIcon()}
      </div>

      {/* Current spend */}
      <div className="flex items-baseline gap-2 mb-2">
        <p className={`text-2xl font-semibold ${getTextColor()}`}>
          {formatCurrency(currentSpent)}
        </p>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          / {formatCurrency(budgetLimit)}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2">
        <div
          data-testid={`budget-progress-${period}`}
          className={`h-2.5 rounded-full transition-all duration-300 ${getProgressColor()}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>

      {/* Percentage and status */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 dark:text-gray-400">{percentage.toFixed(1)}% utilized</span>
        {isOverBudget && (
          <span className="text-xs font-medium text-red-600">
            {formatCurrency(currentSpent - budgetLimit)} over budget
          </span>
        )}
        {isNearBudget && (
          <span className="text-xs font-medium text-yellow-600">
            Approaching limit
          </span>
        )}
        {!isOverBudget && !isNearBudget && (
          <span className="text-xs font-medium text-green-600">
            {formatCurrency(budgetLimit - currentSpent)} remaining
          </span>
        )}
      </div>
    </div>
  );
};

export default BudgetUtilizationCard;
